import { ArrowLeft } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";

import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import homeService from "../../services/HomeService";
import { useQuery } from "@tanstack/react-query";

const MetabolicStabilityDetail = () => {
  const navigate = useNavigate();

  // Use React Query to fetch and cache home page data
  const { data: homeData, isLoading: loading } = useQuery({
    queryKey: ["homePageData"],
    queryFn: () => homeService.getHomePageData(),
  });

  // Get metabolic stability data from API
  const insightData = homeData?.["Health Insights"]?.protein_fiber_ratio_daily;

  const score = insightData?.value ?? 0;
  const status = insightData?.label ?? "Loading...";
  const label = "Protein-Fiber Ratio";

  // Get status color
  const getStatusColor = (status: string) => {
    if (status === "Good" || status === "Excellent") {
      return "text-green-600";
    }
    if (status === "Needs Improvement" || status === "Needs Attention") {
      return "text-orange-600";
    }
    if (status === "Poor") {
      return "text-red-600";
    }
    return "text-muted-foreground";
  };

  // Calculate progress percentage (inverse scale: 0 is best/100%, 10 is worst/0%)
  const getProgressPercentage = (score: number) => {
    if (score <= 0) return 100;
    if (score >= 10) return 0;
    return ((10 - score) / 10) * 100;
  };

  // Get progress bar color based on status
  const getProgressColor = (status: string) => {
    if (status === "Good" || status === "Excellent") {
      return "bg-green-500";
    }
    if (status === "Needs Improvement" || status === "Needs Attention") {
      return "bg-orange-500";
    }
    if (status === "Poor") {
      return "bg-red-500";
    }
    return "bg-orange-500"; // Default
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.HOME)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            {label || "Protein-Fiber Ratio"}
          </h1>
          <div className="h-10 w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="from-warning/10 to-warning/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Protein-Fiber Ratio
            </p>
            <div className="mb-2 text-6xl font-bold text-foreground">
              {typeof score === "number" ? score : 0}
            </div>
            <p className={`text-lg font-semibold ${getStatusColor(status)}`}>
              {status} (Target: ≤ 4.0)
            </p>
            <div className="mt-4 h-3 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className={`h-full rounded-full transition-all ${getProgressColor(status)}`}
                style={{
                  width: `${getProgressPercentage(score)}%`,
                }}
              />
            </div>
          </div>
        </Card>

        {/* What is Metabolic Stability */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Protein-Fiber Ratio?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            The Protein-to-Fiber Ratio measures the balance between protein
            intake and dietary fiber. A balanced ratio supports muscle recovery
            while maintaining gut health and digestive regularity. Too much
            protein relative to fiber can lead to digestive issues, while
            adequate fiber helps with nutrient absorption and sustained energy.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Excellent</span>
              <span className="text-success font-semibold">≤ 2.5</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="font-semibold">≤ 4.0</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Moderate</span>
              <span className="text-warning font-semibold">≤ 6.0</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Poor</span>
              <span className="font-semibold text-destructive">Above 6.0</span>
            </div>
          </div>
        </Card>

        {/* Tips to Improve Metabolic Stability */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            Ways to Improve Your Ratio
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Pair protein sources with high-fiber vegetables at every meal
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Add beans, lentils, or chickpeas as protein-fiber combo foods
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Include chia seeds, flaxseeds, or hemp seeds in smoothies
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Choose whole grains over refined options to boost fiber intake
              </span>
            </li>
          </ul>
        </Card>

        {/* Disclaimer */}

      </div>
    </div>
  );
};

export default MetabolicStabilityDetail;
